import React from 'react'
import { connect } from 'react-redux'
import get from 'lodash/get'
import isNull from 'lodash/isNull'

import * as F from 'discada/dist/Form'
import Loading from 'discada/dist/Loading'
import Icon from 'discada/dist/Icon'

import Config, { ConfigSubmitSet } from 'components/Settings/ConfigForm'
import Fees from 'components/config/quote/Fees'
import Additions from 'components/config/quote/Additions'
import Warranties from 'components/config/quote/Warranties'
import MaintenancePlans from 'components/config/quote/MaintenancePlans'
import Discounts from 'components/config/quote/Discounts'
import { PROFIT_STYLES } from 'reducers/quoteReducers'

import {
  getConfig,
  getRooftopInfo,
  getGlobalConfigIdFromAvailable,
  getIsGettingConfig,
  getConfigLabel,
} from 'selectors/appStatusSelectors'

import { hasWrite } from 'selectors/authSelectors'

import {
  getConfigById,
  saveConfig,
  getAllLenders,
  getAvailableConfigs,
  clearConfig,
} from 'actions/appStatusActions'

import { addToPageState, replacePageState } from 'actions/pageStateActions'

export const WHITE_LIST = [
  'profit',
  'warranties',
  'maintenancePlans',
  'additions',
  'discounts',
  'fees',
]

export class VehicleConfig extends Config {
  category = 'quote'

  refresh() {
    let { getAvailableConfigs, getConfigById, configId } = this.props

    getAvailableConfigs(this.category).then(
      () => configId && getConfigById(configId),
    )
  }

  componentDidMount() {
    this.refresh()
  }

  componentWillUnmount() {
    this.props.clearConfig(this.props.configId)
  }

  processConfigForSave() {
    let { config } = this.state,
      ret = WHITE_LIST.reduce((ret, k) => ({ ...ret, [k]: config[k] }), {})

    return ret
  }

  render() {
    let {
        canDo,
        label,
        configId,
        isGlobalConfig,
        onClose,
        saveConfig,
        addToPageState,
      } = this.props,
      { config, isLoading } = this.state,
      profitStyle = get(this.state.config, 'profit[0].profitStyle', 0)

    if (!canDo) return null
    if (isLoading) return <Loading />

    return (
      <div className="bank-settings">
        <h1>
          <Icon icon="car" /> <strong>Pricing Rules</strong>,{' '}
          <strong>F&amp;I</strong>, and <strong>Profit</strong> by Vehicle
          <small>{label}</small>
        </h1>
        <section>
          <h3>Profit</h3>

          <article className="message is-info is-small">
            <div className="message-body">
              Choose a profit calculation style that fits for you. Selecting{' '}
              <strong>Reserve Amount</strong> will find programs where your back
              end profit is within a set dollar range. Selecting{' '}
              <strong>Rate Bump</strong> will automatically markup either the
              APR or Money Factor by the specified amount. You'll be able to
              change this on the fly for a given quote.
            </div>
          </article>

          <div className="config-row">
            <F.SelectField
              size="small"
              label="Profit Style"
              options={{
                [PROFIT_STYLES.reserve]: 'Target Reserve',
                [PROFIT_STYLES.rateBump]: 'Rate Bump',
              }}
              help="This will be the default profit style used to create quotes"
              value={get(this.state.config, 'profit[0].profitStyle', 0)}
              onChange={({ target }) =>
                this.editValue('profit')(0, 'profitStyle', target.value)
              }
            />
          </div>
          {profitStyle !== PROFIT_STYLES.rateBump && (
            <div className="config-row">
              <F.TextField
                size="small"
                type="number"
                min={0}
                label="Target Reserve"
                help="We will try to find programs with this much paid reserve"
                value={get(
                  this.state.config,
                  'profit[0].desiredPaidReserve',
                  0,
                )}
                onChange={({ target }) =>
                  this.editValue('profit')(
                    0,
                    'desiredPaidReserve',
                    parseFloat(target.value || 0),
                  )
                }
              />
              <F.TextField
                size="small"
                type="number"
                min={0}
                label="Target Reserve +/-"
                help="Add a spread to your target reserve to find more programs"
                value={get(
                  this.state.config,
                  'profit[0].desiredPaidReserveRange',
                  0,
                )}
                onChange={({ target }) =>
                  this.editValue('profit')(
                    0,
                    'desiredPaidReserveRange',
                    parseFloat(target.value || 0),
                  )
                }
              />
            </div>
          )}

          {profitStyle === PROFIT_STYLES.rateBump && (
            <div className="config-row">
              <F.TextField
                size="small"
                type="number"
                min={0}
                label="APR Bump"
                help="Automatically bump APR programs by this amount"
                value={get(this.state.config, 'profit[0].aprMarkup', 0)}
                onChange={({ target }) =>
                  this.editValue('profit')(
                    0,
                    'aprMarkup',
                    isNaN(parseFloat(target.value))
                      ? 0.0
                      : parseFloat(target.value),
                  )
                }
              />
              <F.TextField
                size="small"
                type="number"
                min={0}
                step="any"
                label="Money Factor Bump"
                help="Automatically bump Money Factor programs by this amount"
                value={get(this.state.config, 'profit[0].moneyFactorMarkup', 0)}
                onChange={({ target }) => {
                  this.editValue('profit')(0, 'moneyFactorMarkup', target.value) // TODO this results in a string
                }}
              />
            </div>
          )}

          {profitStyle === PROFIT_STYLES.rateBump && (
            <div className="config-row">
              <F.CheckboxField
                size="small"
                label="Accept lower maximum bump"
                help="Allow for a lower rate bump if your choosen bumps are too high for some lenders (Recommended)"
                checked={
                  !!get(this.state.config, 'profit[0].acceptLowerMaxRate')
                }
                onChange={() =>
                  this.editValue('profit')(
                    0,
                    'acceptLowerMaxRate',
                    !get(this.state.config, 'profit[0].acceptLowerMaxRate'),
                  )
                }
              />
            </div>
          )}
        </section>

        <Warranties
          items={config.warranties}
          addItem={this.addItem}
          editValue={this.editValue}
          removeItem={this.removeItem}
          makeDefault={this.makeDefault}
          hideHelp
        />

        <MaintenancePlans
          items={config.maintenancePlans}
          addItem={this.addItem}
          editValue={this.editValue}
          removeItem={this.removeItem}
          makeDefault={this.makeDefault}
          hideHelp
        />

        <Additions
          items={config.additions}
          addItem={this.addItem}
          editValue={this.editValue}
          removeItem={this.removeItem}
          makeDefault={this.makeDefault}
          hideHelp
        />

        <Fees
          items={config.fees}
          addItem={this.addItem}
          editValue={this.editValue}
          removeItem={this.removeItem}
          makeDefault={this.makeDefault}
        />

        <Discounts
          items={config.discounts}
          addItem={this.addItem}
          editValue={this.editValue}
          removeItem={this.removeItem}
          makeDefault={this.makeDefault}
          hideHelp
        />

        <section>
          <h3>Pricing</h3>
          <div className="row">
            <F.TextField
              size="small"
              type="number"
              label="Freight"
              help="Enter 0 if freight is included in your pricing"
              value={get(config, 'profit[0].freight', 0)}
              onChange={({ target }) =>
                this.editValue('profit')(
                  0,
                  'freight',
                  parseFloat(target.value || 0),
                )
              }
            />
          </div>
        </section>

        <ConfigSubmitSet
          onSubmit={() =>
            saveConfig(
              this.category,
              this.processConfigForSave(),
              configId,
              onClose,
            )
          }
          onDelete={
            isGlobalConfig
              ? null
              : () => addToPageState({ deleteConfig: configId })
          }
          onCancel={onClose}
          onClone={() => addToPageState({ cloneConfig: configId })}
        />
      </div>
    )
  }
}

function mapStateToProps(state, op) {
  let { id } = op,
    config = getConfig(state, id),
    globalConfigId = getGlobalConfigIdFromAvailable(state, 'quote')

  return {
    ...op,
    configId: id,
    config: config || {},
    label: getConfigLabel(state, id),
    canDo: hasWrite(state, 'rooftop.config/quote'),
    rooftopZip: get(getRooftopInfo(state), 'postalCode'),
    isGlobalConfig: id === globalConfigId,
    isLoading: getIsGettingConfig(state) || isNull(config),
  }
}

export default connect(
  mapStateToProps,
  {
    saveConfig,
    getAllLenders,
    addToPageState,
    getConfigById,
    getAvailableConfigs,
    replacePageState,
    clearConfig,
  },
)(VehicleConfig)
